import React, { useEffect, useState, useRef } from 'react';
import { useLeagueCustomization } from '@/hooks/useLeagueCustomization';
import { supabase } from '@/integrations/supabase/client';
import { resolveIsDark } from '@/utils/simpleColorUtils';
import type { CustomizationType } from '@/types/customization';

// Map of customization fonts that are loaded on-demand (not in index.html)
const CUSTOMIZATION_FONTS: Record<string, string> = {
  'Bebas Neue': 'Bebas+Neue',
  'Lato': 'Lato:wght@400;700',
  'Montserrat': 'Montserrat:wght@400;500;600;700',
  'Open Sans': 'Open+Sans:wght@400;500;600;700',
  'Oswald': 'Oswald:wght@400;500;600;700',
  'Playfair Display': 'Playfair+Display:wght@400;700',
  'Poppins': 'Poppins:wght@400;500;600;700',
  'Roboto Condensed': 'Roboto+Condensed:wght@400;700',
  'Roboto': 'Roboto:wght@400;500;700',
};

const loadedFonts = new Set<string>();

function loadCustomizationFonts(designData: any) {
  if (!designData) return;

  const fontFamily = designData.fontFamily || designData.font_family;
  const headingFont = designData.headingFontFamily || designData.heading_font_family;

  const fontsToLoad: string[] = [];

  for (const font of [fontFamily, headingFont]) {
    if (font && CUSTOMIZATION_FONTS[font] && !loadedFonts.has(font)) {
      loadedFonts.add(font);
      fontsToLoad.push(CUSTOMIZATION_FONTS[font]);
    }
  }

  if (fontsToLoad.length > 0) {
    const url = `https://fonts.googleapis.com/css2?${fontsToLoad.map(f => `family=${f}`).join('&')}&display=swap`;
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = url;
    document.head.appendChild(link);
  }
}

interface CustomizationInjectorProps {
  leagueId: string;
  children: React.ReactNode;
}

const CustomizationInjector = ({ leagueId, children }: CustomizationInjectorProps) => {
  const [activeType, setActiveType] = useState<CustomizationType>('standard');
  const hasRunRef = useRef(false);
  
  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const fetchActiveType = async () => {
      const { data } = await supabase
        .from('league_settings')
        .select('active_customization_type')
        .eq('league_id', leagueId)
        .single();
      
      if (data?.active_customization_type) {
        setActiveType(data.active_customization_type as CustomizationType);
      }
    };
    
    fetchActiveType();
  }, [leagueId]);

  const { customization, generateCSS } = useLeagueCustomization(leagueId, activeType);

  useEffect(() => {
    const existingStyle = document.getElementById(`league-customization-${leagueId}`);
    if (existingStyle) {
      existingStyle.remove();
    }

    if (customization && customization.is_active) {
      // Dynamically load any fonts needed by this customization
      loadCustomizationFonts(customization.design_data);

      const css = generateCSS(customization.design_data, customization.custom_css);

      const theme = customization.design_data?.theme as 'light' | 'dark' | 'auto' | undefined;
      const backgroundColor = customization.design_data?.backgroundColor as string | undefined;
      const root = document.documentElement;
      if (theme === 'auto' && !backgroundColor) {
        const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
        root.classList.toggle('dark', !!prefersDark);
      } else {
        // Keep the .dark class in lock-step with the injected surface tokens.
        // A custom background makes the light/dark decision follow the actual
        // background brightness, so we never get white text on a light page.
        root.classList.toggle('dark', resolveIsDark(theme === 'dark' ? 'dark' : 'light', backgroundColor));
      }
      
      if (css) {
        const styleElement = document.createElement('style');
        styleElement.id = `league-customization-${leagueId}`;
        styleElement.textContent = css;
        document.head.appendChild(styleElement);
      }
    }

    return () => {
      const styleElement = document.getElementById(`league-customization-${leagueId}`);
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, [leagueId, customization, activeType]);

  return <>{children}</>;
};

export default CustomizationInjector;
